import React from 'react';
import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ArrowRightOutlined } from '@ant-design/icons';
import { DashboardCustomerPaymentsDto } from '../../dto/report';
import { OrderDto } from '../../dto/order';
import { formatCurrency } from '../../utils/currency';

const { Text, Title } = Typography;

interface CustomerPaymentsPanelProps {
  customerPayments: DashboardCustomerPaymentsDto;
}

export default function CustomerPaymentsPanel({ customerPayments }: CustomerPaymentsPanelProps) {
  const { t } = useTranslation(['dashboard', 'orders']);

  const orderLink = (order: OrderDto) => (
    <Link to={`/app/orders/${order.id}`}>{order.displayOrderNumber || order.orderNumber}</Link>
  );

  const columns: ColumnsType<OrderDto> = [
    {
      title: t('dashboard:customerPayments.order'),
      key: 'orderNumber',
      render: (_, order) => orderLink(order),
    },
    {
      title: t('dashboard:customerPayments.customer'),
      key: 'customer',
      render: (_, order) => order.customer?.id ? (
        <Link to={`/app/customers/${order.customer.id}`}>{order.customerName || order.customer.name}</Link>
      ) : (
        order.customerName || '-'
      ),
    },
    {
      title: t('dashboard:customerPayments.dueDate'),
      key: 'dueDate',
      render: (_, order) => order.customerPaymentDueDate ? dayjs(order.customerPaymentDueDate).format('YYYY-MM-DD') : '-',
    },
    {
      title: t('dashboard:customerPayments.status'),
      key: 'status',
      render: (_, order) => (
        <Tag color={order.customerPaymentStatus === 'PAID' ? 'green' : 'orange'}>
          {t(`orders:customerPaymentStatus.${order.customerPaymentStatus || 'PENDING'}`)}
        </Tag>
      ),
    },
  ];

  return (
    <section className="dashboard-section dashboard-payments-band">
      <div className="dashboard-section__header">
        <Title level={4}>{t('dashboard:customerPayments.title')}</Title>
        <Text type="secondary">
          {t('dashboard:customerPayments.horizon', { days: customerPayments.horizonDays })}
        </Text>
      </div>
      <div className="dashboard-payments-grid">
        <div className="dashboard-payment-summary">
          <Text strong>{t('dashboard:customerPayments.expected')}</Text>
          <div className="dashboard-payment-amounts">
            {customerPayments.expectedByCurrency.length > 0 ? (
              customerPayments.expectedByCurrency.map((item) => (
                <span key={item.currency} className="dashboard-payment-pill">
                  {formatCurrency(item.amount, item.currency)}
                </span>
              ))
            ) : (
              <Text type="secondary">{t('dashboard:customerPayments.none')}</Text>
            )}
          </div>
          <Text type="secondary">{t('dashboard:customerPayments.expectedHint')}</Text>
        </div>
        <div className="dashboard-payment-summary dashboard-payment-summary--danger">
          <Text strong>{t('dashboard:customerPayments.overdue')}</Text>
          <div className="dashboard-payment-amounts">
            {customerPayments.overdueByCurrency.length > 0 ? (
              customerPayments.overdueByCurrency.map((item) => (
                <span key={item.currency} className="dashboard-payment-pill dashboard-payment-pill--danger">
                  {formatCurrency(item.amount, item.currency)}
                </span>
              ))
            ) : (
              <Text type="secondary">{t('dashboard:customerPayments.none')}</Text>
            )}
          </div>
          <Text type="secondary">{t('dashboard:customerPayments.overdueHint')}</Text>
        </div>
      </div>
      <Table
        dataSource={[...customerPayments.overdue, ...customerPayments.upcoming].slice(0, 6)}
        columns={columns}
        rowKey="id"
        pagination={false}
        size="small"
        locale={{ emptyText: t('dashboard:customerPayments.noOrders') }}
      />
      <div className="dashboard-payments-footer">
        <Link to="/app/orders">
          {t('dashboard:viewAll')} <ArrowRightOutlined />
        </Link>
      </div>
    </section>
  );
}
